import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { HellocleverResponse } from 'src/types';
import { openChat } from 'src/utils';
import { Copy } from 'src/components/svg/Copy';
import { getCurrencyIcon } from 'react-easyrocket';
import { Input } from 'src/components/Inputs/Input';
import { ModalWrapper } from 'src/components/ModalWrapper';
import { useModalContext } from 'src/hooks/useModalContext';
import { THREE_SECONDS } from 'src/constants';

type HellocleverResultModalProps = HellocleverResponse & {
  paymentMethodName?: string;
};

const HellocleverResultModal = () => {
  const { t } = useTranslation();
  const { modalProps } = useModalContext<HellocleverResultModalProps>();
  const [isCopiedEmail, setIsCopiedEmail] = useState<boolean>(false);
  const [isCopiedNonce, setIsCopiedNonce] = useState<boolean>(false);

  const currencySign = useMemo(() => {
    return modalProps?.currency ? getCurrencyIcon(modalProps?.currency) : '';
  }, [modalProps?.currency]);

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(modalProps?.account ? modalProps.account : '');
    setIsCopiedEmail(true);
  };

  const handleCopyNonce = () => {
    navigator.clipboard.writeText(modalProps?.nonce ? modalProps.nonce : '');
    setIsCopiedNonce(true);
  };

  useHideByTheTime(isCopiedEmail, setIsCopiedEmail);
  useHideByTheTime(isCopiedNonce, setIsCopiedNonce);

  return (
    <ModalWrapper>
      <div className="w-[340px] r_xsm:w-full sm:w-[400px] flex flex-col items-center py-[39px] px-5 gap-[15px] text-b_tertiary_text text-[15px] font-medium">
        <div className="text-[20px] font-bold text-b_simple_text uppercase text-center leading-[110%]">
          {t('helloCleverResult.topUp', { paymentMethodName: modalProps?.paymentMethodName })}
        </div>

        <div className="text-[20px] font-bold text-b_simple_text uppercase text-center leading-[110%]">
          {currencySign} {Number(modalProps?.amount).toFixed(2)}
        </div>

        <p>
          {t('helloCleverResult.send', {
            amount: Number(modalProps?.amount).toFixed(2),
            currency: currencySign,
            paymentMethodName: modalProps?.paymentMethodName
          })}
        </p>

        <div
          className="flex flex-row relative !w-full !max-w-[400px] cursor-pointer"
          onClick={handleCopyEmail}>
          <Input
            id="account-input"
            type="text"
            value={modalProps?.account}
            readOnly
            rightIcon={<Copy />}
            className="!cursor-pointer text-i_placeholder"
          />
          <Tooltip
            anchorSelect="#account-input"
            className="bg-color-link text-dpip_text rounded-4xl"
            place="top"
            isOpen={isCopiedEmail}>
            <div>{t('helloCleverResult.copied')}</div>
          </Tooltip>
        </div>

        <div>{t('helloCleverResult.nonceText')}</div>

        <div>{t('helloCleverResult.nonceAlert')}</div>

        <div
          className="flex flex-row relative !w-full !max-w-[400px] cursor-pointer"
          onClick={handleCopyNonce}>
          <Input
            id="nonce-input"
            type="text"
            value={modalProps?.nonce}
            readOnly
            rightIcon={<Copy />}
            className="!cursor-pointer text-i_placeholder"
          />
          <Tooltip
            anchorSelect="#nonce-input"
            className="bg-color-link text-dpip_text rounded-4xl"
            place="top"
            isOpen={isCopiedNonce}>
            <div>{t('helloCleverResult.copied')}</div>
          </Tooltip>
        </div>

        <div className="text-dpip_text_secondary">
          <Trans
            i18nKey="helloCleverResult.modalInfoGeneral"
            components={{
              '1': <button className="underline text-b_simple_text" onClick={openChat} />
            }}
          />
        </div>
        <div className="pb-4 text-dpip_text_secondary">
          <Trans
            i18nKey="helloCleverResult.modalInfo"
            components={{ ul: <ul className="list-disc pl-5" />, li: <li /> }}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

function useHideByTheTime(state: boolean, setState: Dispatch<SetStateAction<boolean>>) {
  useEffect(() => {
    const timer = setTimeout(() => {
      state && setState(false);
    }, THREE_SECONDS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
}

// eslint-disable-next-line no-restricted-exports
export default HellocleverResultModal;
